/**
 * Input security utilities for preventing XSS and other attacks
 */

/**
 * Escapes HTML characters to prevent XSS attacks
 */
export function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
    .replace(/\//g, "&#x2F;");
}

/**
 * Removes potentially dangerous characters from user input
 */
export function sanitizeInput(input: string): string {
  return input
    .replace(/[<>]/g, '') // Remove < and >
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers like onclick=
    .replace(/style\s*=/gi, '') // Remove style attributes
    .trim()
    .slice(0, 1000); // Limit length to prevent buffer overflow
}

/**
 * Validates email format with comprehensive checks
 */
export function validateEmail(email: string): { isValid: boolean; error?: string } {
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }

  // Length check
  if (email.length > 254) {
    return { isValid: false, error: 'Email is too long' };
  }

  // Basic format check
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Invalid email format' };
  }

  // Check for dangerous patterns
  if (/[<>"]/.test(email)) {
    return { isValid: false, error: 'Email contains invalid characters' };
  }

  // Check for multiple @ symbols
  if ((email.match(/@/g) || []).length !== 1) {
    return { isValid: false, error: 'Email must contain exactly one @ symbol' };
  }

  return { isValid: true };
}

/**
 * Validates password strength
 */
export function validatePassword(password: string): { 
  isValid: boolean; 
  score: number; 
  errors: string[];
  suggestions: string[];
} {
  const errors: string[] = [];
  const suggestions: string[] = [];
  let score = 0;

  if (!password) {
    return { 
      isValid: false, 
      score: 0, 
      errors: ['Password is required'], 
      suggestions: ['Enter a password'] 
    };
  }

  // Length checks
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
    suggestions.push('Use at least 8 characters');
  } else {
    score += 1;
  }

  if (password.length > 128) {
    errors.push('Password is too long (max 128 characters)');
  }

  // Character variety checks
  if (!/[a-z]/.test(password)) {
    suggestions.push('Add lowercase letters');
  } else {
    score += 1;
  }

  if (!/[A-Z]/.test(password)) {
    suggestions.push('Add uppercase letters');
  } else {
    score += 1;
  }

  if (!/[0-9]/.test(password)) {
    suggestions.push('Add numbers');
  } else {
    score += 1;
  }

  if (!/[^a-zA-Z0-9]/.test(password)) {
    suggestions.push('Add special characters');
  } else {
    score += 1;
  }

  // Common password checks
  const commonPasswords = [
    'password', '123456', '123456789', 'qwerty', 'abc123', 
    'password123', 'admin', 'letmein', 'welcome', 'monkey'
  ];
  
  if (commonPasswords.includes(password.toLowerCase())) {
    errors.push('Password is too common');
    suggestions.push('Use a unique password');
    score = Math.max(0, score - 2);
  }

  // Sequential character check
  if (/123|abc|qwe/i.test(password)) {
    suggestions.push('Avoid sequential characters');
    score = Math.max(0, score - 1);
  }

  return {
    isValid: errors.length === 0 && score >= 3,
    score: Math.min(5, score),
    errors,
    suggestions
  };
}

/**
 * Sanitizes URL to prevent open redirects
 */
export function sanitizeRedirectUrl(url: string, allowedDomains: string[] = []): string | null {
  try {
    // Remove any potentially dangerous characters
    const cleanUrl = url.replace(/[<>"']/g, '');
    
    // If it's a relative URL starting with /, it's safe
    if (cleanUrl.startsWith('/') && !cleanUrl.startsWith('//')) {
      return cleanUrl;
    }

    // If it's an absolute URL, check the domain
    const urlObj = new URL(cleanUrl);
    
    // Only allow HTTPS
    if (urlObj.protocol !== 'https:') {
      return null;
    }

    // Check if domain is in allowlist
    const isAllowedDomain = allowedDomains.some(domain => 
      urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain)
    );

    return isAllowedDomain ? cleanUrl : null;
  } catch {
    // Invalid URL
    return null;
  }
}

/**
 * Rate limiting utility for preventing abuse
 */
class RateLimiter {
  private attempts: Map<string, number[]> = new Map();
  
  isAllowed(key: string, maxAttempts: number, windowMs: number): boolean {
    const now = Date.now();
    const windowStart = now - windowMs;
    
    // Get existing attempts for this key
    let keyAttempts = this.attempts.get(key) || [];
    
    // Remove attempts outside the window
    keyAttempts = keyAttempts.filter(time => time > windowStart);
    
    // Check if we're under the limit
    if (keyAttempts.length >= maxAttempts) {
      return false;
    }
    
    // Add this attempt
    keyAttempts.push(now);
    this.attempts.set(key, keyAttempts);
    
    return true;
  }
  
  reset(key: string): void {
    this.attempts.delete(key);
  }
}

export const rateLimiter = new RateLimiter();